import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MAX_FILE_SIZE } from '../../../constants/statics';
import React, { Component } from 'react';
import _ from 'lodash';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import Pretty from 'prettysize';
import classnames from 'classnames/bind';
import Icon from '../../elementals/icon';
import Text from '../../elementals/text';
import DownloadLink from '../../downloadLink';
import styles from './styles.scss';
const cx = classnames.bind(styles);
export const PDF = 'application/pdf';
export const ERROR_MESSAGE_FILE_REQUIRED = 'File is required.';
export const ERROR_MESSAGE_FILE_TYPE_PDF_ONLY = 'Only pdf files accepted.';
export const ERROR_MESSAGE_FILE_TYPE_NOT_ACCEPTED = 'File type not accepted.';
export const ERROR_MESSAGE_TOO_MANY_FILES = maxFiles => `Too many files specified.  You are limited to ${maxFiles} ${pluralize('file', maxFiles)}.`;
export const ERROR_MESSAGE_FILE_SIZE_LIMIT_REACHED = maxFileSize => `File size is limited to ${Pretty(maxFileSize)}.`;
export const ERROR_MESSAGE_DUPLICATE_FILE_NAMES = fileNames => {
    return `You already have a file name:\n ${fileNames.join('\n')}`;
};
class FileDropzone extends Component {
    static propTypes = {
        files: PropTypes.array.isRequired,
        maxFileSize: PropTypes.number.isRequired,
        maxNumberOfFiles: PropTypes.number,
        onChange: PropTypes.func.isRequired,
        acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
        remoteError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        errorMessageFileRequired: PropTypes.string,
        errorMessageFileTypePDFOnly: PropTypes.string,
        errorMessageFileTypeNotAccepted: PropTypes.string,
        errorMessageFileSizeLimitReached: PropTypes.func,
        errorMessageTooManyFiles: PropTypes.func,
        errorMessageDuplicateFileNames: PropTypes.func,
        disabled: PropTypes.bool,
        compact: PropTypes.bool,
        multiple: PropTypes.bool,
        required: PropTypes.bool,
        className: PropTypes.string,
        resources: PropTypes.arrayOf(PropTypes.shape({
            urn: PropTypes.string,
        })),
        embeddedContext: PropTypes.string,
    };
    static defaultProps = {
        acceptedFileTypes: [PDF],
        maxFileSize: MAX_FILE_SIZE,
        maxNumberOfFiles: undefined,
        remoteError: false,
        errorMessageFileRequired: ERROR_MESSAGE_FILE_REQUIRED,
        errorMessageFileTypePDFOnly: ERROR_MESSAGE_FILE_TYPE_PDF_ONLY,
        errorMessageFileTypeNotAccepted: ERROR_MESSAGE_FILE_TYPE_NOT_ACCEPTED,
        errorMessageFileSizeLimitReached: ERROR_MESSAGE_FILE_SIZE_LIMIT_REACHED,
        errorMessageTooManyFiles: ERROR_MESSAGE_TOO_MANY_FILES,
        errorMessageDuplicateFileNames: ERROR_MESSAGE_DUPLICATE_FILE_NAMES,
        disabled: false,
        compact: false,
        multiple: false,
        required: false,
        embeddedContext: 'browser',
    };
    state = {
        error: null,
        files: this.props.files,
        dropzoneActive: false,
    };
    static getDerivedStateFromProps(nextProps, prevState) {
        let files = prevState.files;
        if (!_.isEqual(nextProps.files, prevState.files))
            files = nextProps.files;
        return {
            files,
        };
    }
    onDrop = (filesToUpload, rejectedFiles, e) => {
        const { onChange, acceptedFileTypes, maxFileSize, maxNumberOfFiles, multiple, errorMessageFileTypePDFOnly, errorMessageFileTypeNotAccepted, errorMessageFileSizeLimitReached, errorMessageTooManyFiles, errorMessageDuplicateFileNames, } = this.props;
        this.onDragLeave();
        if (!filesToUpload.every(file => acceptedFileTypes.includes(file.type))) {
            const error = _.isEqual(acceptedFileTypes, [PDF])
                ? errorMessageFileTypePDFOnly
                : errorMessageFileTypeNotAccepted;
            this.setState({ error });
            return;
        }
        if (filesToUpload.some(file => file.size > maxFileSize)) {
            this.setState({ error: errorMessageFileSizeLimitReached(maxFileSize) });
            return;
        }
        let filesUploaded = this.state.files.slice() || [];
        const uploadedFileNames = filesUploaded.map(file => file.name);
        const toUploadFileNames = filesToUpload.map(file => file.name);
        const duplicateFileNames = _.intersection(uploadedFileNames, toUploadFileNames);
        if (!_.isEmpty(duplicateFileNames)) {
            this.setState({
                error: errorMessageDuplicateFileNames(duplicateFileNames),
            });
            return;
        }
        if (multiple) {
            filesUploaded.push(...filesToUpload);
            if (maxNumberOfFiles && filesUploaded.length > maxNumberOfFiles) {
                this.setState({ error: errorMessageTooManyFiles(maxNumberOfFiles) });
                return;
            }
        }
        else {
            filesUploaded = !_.isEmpty(filesToUpload) ? filesToUpload : filesUploaded;
        }
        this.setState({ error: null, files: filesUploaded });
        onChange(filesUploaded);
    };
    onDragEnter = () => this.setState({ dropzoneActive: true });
    onDragLeave = () => this.setState({ dropzoneActive: false });
    isBlob = file => _.startsWith(file.preview, 'blob:');
    renderDroppedFiles = files => {
        const { onChange, disabled, required, errorMessageFileRequired, embeddedContext, resources, } = this.props;
        const isOnWebView = embeddedContext === 'webview';
        return (files.length > 0 &&
            Array.isArray(files) && (_jsx("ul", { className: cx('uploaded-files'), children: files.map((file, i) => {
                return (_jsxs("li", { children: [isOnWebView && resources?.[i] ? (_jsx("div", { className: cx('file-name'), children: _jsx(DownloadLink, { embeddedContext: embeddedContext, resource: resources[i], children: file.name }) })) : (_jsx("a", { href: file.preview, className: cx('file-name'), download: this.isBlob(file), children: file.name })), _jsx(Icon, { className: cx('dropped-file-icons', disabled && 'dropzone-disabled'), color: 'gray', iconStyle: 'regular', name: 'trash', size: 'medium', onClick: () => {
                                if (disabled)
                                    return;
                                const newFiles = files.slice();
                                newFiles.splice(i, 1);
                                onChange(newFiles, i);
                                const errorMsg = required && files.length === 0
                                    ? errorMessageFileRequired
                                    : null;
                                this.setState({ error: errorMsg, files: newFiles });
                            } })] }, i));
            }) })));
    };
    getIconColor = () => {
        const { disabled, remoteError } = this.props;
        const { error, dropzoneActive } = this.state;
        if (disabled) {
            return 'grayMedium';
        }
        else if (dropzoneActive) {
            return 'info';
        }
        else if (error || remoteError) {
            return 'error';
        }
        else {
            return 'black';
        }
    };
    render() {
        const { remoteError, disabled, label, errorMessageFileRequired, errorMessageFileTypePDFOnly, errorMessageFileTypeNotAccepted, errorMessageFileSizeLimitReached, errorMessageTooManyFiles, errorMessageDuplicateFileNames, onChange, acceptedFileTypes, maxNumberOfFiles, maxFileSize, compact, className: customClassName, ...dropzoneProps } = this.props;
        const { error, dropzoneActive, files } = this.state;
        return (_jsxs("div", { children: [this.renderDroppedFiles(files), _jsx(Dropzone, { className: cx('dropzone', dropzoneActive ? 'dropzone-active' : '', error || remoteError ? 'dropzone-error' : '', disabled ? 'dropzone-disabled' : '', compact ? 'compact' : '', customClassName), activeStyle: {
                        border: '2px dashed #008cbf',
                    }, disabled: disabled, onDrop: this.onDrop, onDragEnter: this.onDragEnter, onDragLeave: this.onDragLeave, inputProps: {
                        'data-elm-id': 'dropzone_input',
                    }, ...dropzoneProps, children: _jsxs("div", { className: cx('dropzone-text'), children: [_jsx(Icon, { color: 'grayMediumLight', iconStyle: 'regular', name: 'cloud-arrow-up', size: 'big' }), _jsxs(Text, { as: 'span', color: 'gray', size: 'tiny', weight: 'black', children: ["Choose a file", _jsx(Text, { as: 'span', className: cx('dropzone-or'), color: 'gray', size: 'tiny', children: ' or drag it here' }), _jsx(Text, { as: 'span', color: 'gray', size: 'tiny', children: ' (PDF)' })] })] }) }), (error || remoteError) && (_jsx("div", { className: cx('dropzone-error-message'), "data-elm-id": 'dropzone_error_message', children: error || remoteError }))] }));
    }
}
export default FileDropzone;
//# sourceMappingURL=index.js.map